import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import apiService from '../services/api';
import { formatNumberForChart } from '../utils/formatNumber';

const DepartmentDetails = () => {
  const { id } = useParams();
  const [department, setDepartment] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDepartmentDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch department details
      const departmentResponse = await apiService.departments.getById(id);
      setDepartment(departmentResponse.data);

      // Fetch all metrics for this department
      const metricsResponse = await apiService.metrics.getAll({
        department_id: id
      });
      setMetrics(metricsResponse.data);

      // Fetch all areas for this department
      const areasResponse = await apiService.areas.getAll({
        department_id: id
      });
      setAreas(areasResponse.data);

    } catch (err) {
      setError('Failed to load department details');
      console.error('Error fetching department details:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDepartmentDetails();
  }, [fetchDepartmentDetails]);

  // Fetch metric values for each metric to create charts
  const [metricValues, setMetricValues] = useState({});
  const [chartsLoading, setChartsLoading] = useState(false);

  useEffect(() => {
    const fetchMetricValues = async () => {
      if (metrics.length === 0) return;
      
      setChartsLoading(true);
      try {
        const valuesPromises = metrics.map(async (metric) => {
          try {
            const response = await apiService.metricValues.getAll({
              metric_id: metric.id,
              page: 1,
              page_size: 10, // Get last 10 values for chart
              sort_by: 'value_date',
              sort_dir: 'desc'
            });
            return { metricId: metric.id, values: response.data.items || [] };
          } catch (err) {
            console.error(`Error fetching values for metric ${metric.id}:`, err);
            return { metricId: metric.id, values: [] };
          }
        });

        const results = await Promise.all(valuesPromises);
        const valuesMap = {};
        results.forEach(({ metricId, values }) => {
          valuesMap[metricId] = values;
        });
        setMetricValues(valuesMap);
      } catch (err) {
        console.error('Error fetching metric values:', err);
      } finally {
        setChartsLoading(false);
      }
    };

    fetchMetricValues();
  }, [metrics]);

  // Prepare chart data for each metric
  const getChartData = (metric) => {
    const values = metricValues[metric.id] || [];
    return values.slice(0, 10).map(value => ({
      date: new Date(value.value_date).toLocaleDateString(),
      actual: parseFloat(value.value),
      target: value.target ? parseFloat(value.target) : null
    })).reverse(); // Reverse to show oldest to newest
  };

  // Helper function to determine if a value is on the wrong side of the target
  const isValueOnWrongSide = (actual, target, higherIsBetter) => {
    if (actual === null || actual === undefined || target === null || target === undefined) {
      return false;
    }
    if (higherIsBetter) {
      // Higher is better: wrong side if actual < target
      return actual < target;
    } else {
      // Lower is better: wrong side if actual > target
      return actual > target;
    }
  };

  // Calculate smart Y-axis domain to handle negative values
  const getYAxisDomain = (chartData) => {
    if (!chartData || chartData.length === 0) {
      return ['auto', 'auto'];
    }

    // Extract all numeric values (actual and target)
    const allValues = chartData
      .flatMap(d => [d.actual, d.target])
      .filter(v => v !== null && v !== undefined && !isNaN(v));

    if (allValues.length === 0) {
      return ['auto', 'auto'];
    }

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);

    // If all values are negative, include 0 at the top
    if (max < 0) {
      return [min * 1.1, 0]; // Add 10% padding below minimum
    }

    // If values span negative and positive, include 0 in the middle
    if (min < 0 && max > 0) {
      const range = Math.max(Math.abs(min), Math.abs(max));
      return [min - range * 0.1, max + range * 0.1]; // Add 10% padding on both sides
    }

    // Default behavior for all positive values
    return ['auto', 'auto'];
  };

  // Get area name for a metric
  const getAreaName = (metric) => {
    if (!metric.area_id) return null;
    const area = areas.find(a => a.id === metric.area_id);
    return area ? area.name : null;
  };

  if (loading) {
    return <div className="loading">Loading department details...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!department) {
    return <div className="error">Department not found</div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <Link to="/departments" className="btn btn-secondary btn-small">
            ← Back to Departments
          </Link>
          <h1 className="page-title">{department.name}</h1>
          <p className="text-muted">Department Details</p>
        </div>
      </div>

      {/* Department Information - Horizontal Layout */}
      <div className="details-card" style={{ marginBottom: '2rem' }}>
        <h3>Department Information</h3>
        <div style={{ 
          display: 'flex', 
          gap: '2rem', 
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <strong>Name:</strong>
            <span>{department.name}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <strong>Created:</strong>
            <span>{new Date(department.created_at).toLocaleDateString()}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <strong>Metrics Count:</strong>
            <span>{metrics.length}</span>
          </div>
        </div>
      </div>

      {/* Performance Charts for each metric */}
      {metrics.length > 0 && (
        <div className="details-card">
          <h3>Performance Charts</h3>
            {chartsLoading ? (
              <div className="loading">Loading charts...</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem' }}>
                {metrics.map(metric => {
                  const chartData = getChartData(metric);
                  const areaName = getAreaName(metric);
                  return (
                    <div key={metric.id} style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '1rem' }}>
                      {areaName && (
                        <div style={{
                          marginBottom: '0.5rem',
                          fontSize: '0.875rem',
                          color: '#6c757d',
                          fontWeight: 500
                        }}>
                          {areaName}
                        </div>
                      )}
                      <Link 
                        to={`/metrics/${metric.id}`}
                        style={{ 
                          textDecoration: 'none',
                          display: 'block'
                        }}
                      >
                        <h4 style={{ 
                          marginBottom: '1rem', 
                          color: '#034955',
                          cursor: 'pointer',
                          transition: 'color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.color = '#032E36'}
                        onMouseLeave={(e) => e.target.style.color = '#034955'}
                        >
                          {metric.name}
                        </h4>
                      </Link>
                      {chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis 
                              domain={getYAxisDomain(chartData)}
                              tickFormatter={(value) => formatNumberForChart(value, metric.display_format || 'number', metric.unit)}
                            />
                            <Tooltip 
                              formatter={(value, name) => {
                                // In Recharts, the 'name' parameter is the 'name' prop from the component
                                // Bar has name="Actual", Line has name="Target"
                                const label = name === 'Actual' ? 'Actual' : 'Target';
                                const formattedValue = formatNumberForChart(
                                  value, 
                                  metric.display_format || 'number',
                                  metric.unit
                                );
                                return [formattedValue, label];
                              }}
                            />
                            <Legend />
                            <Bar dataKey="actual" name="Actual">
                              {chartData.map((entry, index) => {
                                const isWrongSide = isValueOnWrongSide(
                                  entry.actual,
                                  entry.target,
                                  metric.higher_is_better !== false // Default to true if not set
                                );
                                return (
                                  <Cell key={`cell-${index}`} fill={isWrongSide ? '#DC3545' : '#034955'} />
                                );
                              })}
                            </Bar>
                            <Line 
                              type="monotone" 
                              dataKey="target" 
                              stroke="#DE37A4" 
                              strokeWidth={3}
                              dot={{ fill: '#DE37A4', strokeWidth: 2, r: 4 }}
                              name="Target" 
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-muted">No data available for chart</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default DepartmentDetails;